import React from "react"
import styled from "@emotion/styled";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from "@mui/styles";
import {
  TimelineChangeList,
  TimelineChangeRow,
  TimelineSection,
  TimelineSectionTitle,
  TimelineTagPill,
} from "../../TimelineLayout"

const labelOverrides = {
  "Last Sale Recording Document ID": "Last Sale Recording Doc ID",
  "Owner One Ownership Rights": "Owner 1 Ownership Rights",
  "Close Price Description": "Close Price Source",
}

const currencyLabels = [
  "Assessed Value",
  "Land Value",
  "Improvements Value",
  "Close Price",
  "Assessed Land Value",
  "Assessed Improvement Value",
  "Primary Mortgage Amount",
]

const decimalLabels = ["Latitude", "Longitude", "Rooms", "Bedrooms", "Bathrooms Full", "Bathrooms Total"]
const areaLabels = ["Living Area", "Garage Area", "Building Area Total", "Gross Area", "Lot Size Square Feet"]

const formatValue = (discrepancy, value) => {
  if (!value || value === 0 || value.length === 0) {
    return <EmptyValue>-empty-</EmptyValue>
  }

  if (currencyLabels.includes(discrepancy.label)) {
    return `$${parseFloat(value).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}`
  }

  if (discrepancy.label === "Tax Annual Amount") {
    return `$${parseFloat(value).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 2})}`
  }

  if (decimalLabels.includes(discrepancy.label)) {
    return parseFloat(value)
  }

  if (areaLabels.includes(discrepancy.label)) {
    return `${parseFloat(value).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})} sq ft`
  }

  if (discrepancy.label === "Primary Mortgage Interest Rate") {
    return `${parseFloat(value).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 1})}%`
  }

  return value
}

const DiscrepancyRows = ({items, tierClass, type}) => {
  const theme = useTheme()

  if (!items || items.length === 0) {
    return null
  }

  return items.map((discrepancy) => (
    <TimelineChangeRow key={discrepancy.label}>
      <span className="timeline-change-label">
        <TimelineTagPill className={type === 'resolved' ? "success" : tierClass}>
          {type === 'resolved' && <CheckCircleIcon style={{color: `${theme.palette.primary.dark}`, width: '1rem', height: '1rem'}} />}
          {labelOverrides[discrepancy.label] || discrepancy.label}
        </TimelineTagPill>
      </span>
      <span className="timeline-change-value">
        {discrepancy.publicRecordValue !== null ?
          <>
            <span>{formatValue(discrepancy, discrepancy.publicRecordValue)}</span>
            {discrepancy.originalValue && discrepancy.originalValue !== 0 && discrepancy.originalValue.length !== 0 &&
              <span> <span style={{fontWeight: '300', margin: '0 0.2rem'}}>from</span> {formatValue(discrepancy, discrepancy.originalValue)}</span>
            }
          </>
        :
          <EmptyValuePhrase>
            <EmptyValue>-empty-</EmptyValue>
            <FromText>from</FromText>
            <span>{discrepancy.originalValue}</span>
          </EmptyValuePhrase>
        }
      </span>
    </TimelineChangeRow>
  ))
}

const DiscrepanciesDetected = ({ type, tier1, tier2, tier3  }) => (
  <TimelineSection>
    <TimelineSectionTitle>
      {type === 'discrepancy' ?
        'New Discrepancies Identified'
      : type === 'update' ?
        'Public Record updates on Existing Discrepancies'
      :
        'Public Record updates that Match Existing Records'
      }
    </TimelineSectionTitle>
    <TimelineChangeList>
      <DiscrepancyRows items={tier1} tierClass="tier-one" type={type} />
      <DiscrepancyRows items={tier2} tierClass="tier-two" type={type} />
      <DiscrepancyRows items={tier3} tierClass="tier-three" type={type} />
    </TimelineChangeList>
  </TimelineSection>
)

const EmptyValuePhrase = styled.span`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  align-items: baseline;
  gap: 0.28rem;
  flex-wrap: wrap;
  vertical-align: baseline;
`

const EmptyValue = styled.span(({ theme }) => `
  display: inline;
  color: ${theme.palette.common.grayText};
  font-style: italic;
  font-weight: 600;
  white-space: nowrap;
`)

const FromText = styled.span`
  display: inline;
  font-weight: 300;
  white-space: nowrap;
`

export default DiscrepanciesDetected
